/**
 * assessmentDraft — the in-progress answers of ONE career check-in, kept in
 * sessionStorage until a submit succeeds (platform feedback 2026-10-21, O24:
 * "did the whole test, then an error — none of my answers saved, I had to
 * redo it").
 *
 * Keyed by the SIGNED-IN user, the instrument version and the acting student:
 *
 *   career_assessment_draft:<userId>:<key>@<version>:<studentId|self>
 *
 * The user id is the shared-tab boundary (Codex audit of PR 4, finding 1): a
 * student who logs out and hands the tab over must not leave answers that the
 * next account can see or submit as its own. Without a user id nothing is
 * drafted (the key is null and every access is a no-op). Logout purges the
 * whole namespace as well (App.js clearLocalSession → purgeAssessmentDrafts),
 * the same wipe discipline as the two advisors' drafts.
 *
 * sessionStorage, not localStorage: a tab's own scratch, gone when the tab
 * closes — a minor's answers never outlive the session on a shared machine.
 * Storage can be unavailable (private windows, a full quota): every access is
 * guarded, and the draft is only a convenience — the submit is the record.
 */
export const ASSESSMENT_DRAFT_PREFIX = 'career_assessment_draft:';

export function draftKey(instrument, { userId = null, studentId = null } = {}) {
  if (!userId || !instrument) return null;
  return `${ASSESSMENT_DRAFT_PREFIX}${userId}:${instrument.key}@${instrument.version}:${studentId || 'self'}`;
}

export function readDraft(key) {
  if (!key) return {};
  try {
    const raw = window.sessionStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function writeDraft(key, answers) {
  if (!key) return;
  try {
    if (Object.keys(answers).length === 0) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, JSON.stringify(answers));
  } catch {
    /* storage unavailable: the draft is a convenience, the submit is the record */
  }
}

export function clearDraft(key) {
  if (!key) return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* nothing to clear */
  }
}

/** Every check-in draft on this tab, whoever wrote it — the logout wipe. */
export function purgeAssessmentDrafts() {
  try {
    const doomed = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const k = window.sessionStorage.key(i);
      if (k && k.startsWith(ASSESSMENT_DRAFT_PREFIX)) doomed.push(k);
    }
    doomed.forEach((k) => window.sessionStorage.removeItem(k));
  } catch {
    /* best-effort: nothing further depends on this succeeding */
  }
}
